"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useZStore } from "@/utils/z-store";
import cn from "@/utils/cn";
import TextComponent from "@/app/_components/TextComponent";
import {
  ReportIcon,
  ReorderIcon,
  DropDownEllipse,
  DropdownCreateNewIcon,
  DropdownRadioUnselectedIcon,
  DropdownRadioSelectedIcon,
  DropdownSaveIcon,
  DropdownArrowDownIcon,
  DropdownArrowUpIcon,
} from "@/assets/svg-icons/svg-icons";
import axios from "axios";

interface CustomReportDropdown {
  invertColors?: boolean;
  truncateLength?: number;
  disabled?: boolean;
  isEditPage?: boolean;
}

const CustomReportDropdown: React.FC<CustomReportDropdown> = ({
  invertColors = false,
  truncateLength = 15,
  disabled = false,
  isEditPage = false,
}) => {
  const { data, actions } = useZStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newReportName, setNewReportName] = useState("Create New Report");
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [editingReportName, setEditingReportName] = useState("");
  const [showOptionsForId, setShowOptionsForId] = useState<string | null>(null);
  const [hoveredReportId, setHoveredReportId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedReportId = data.selectedCustomReport;
  const customReports = data.customReports || [];

  const selectedReport =
    customReports.find((r) => r.id === selectedReportId)?.name || null;

  // Check if the selectedReportId exists in customReports
  const isSelectedReportValid = customReports.some(
    (r) => r.id === selectedReportId,
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        resetState();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if ((isCreatingNew || editingReportId) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingNew, editingReportId]);

  // Auto-select the first report if no report is selected or if selected report is invalid
  useEffect(() => {
    if (
      customReports.length > 0 &&
      (!selectedReportId || !isSelectedReportValid)
    ) {
      actions.setSelectedCustomReport(customReports[0].id);
    }
  }, [selectedReportId, customReports, actions, isSelectedReportValid]);

  const resetState = useCallback(() => {
    setIsOpen(false);
    setIsCreatingNew(false);
    setNewReportName("Create New Report");
    setShowOptionsForId(null);
    setEditingReportId(null);
    setEditingReportName("");
    setHoveredReportId(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setReorderMode(false);
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleReportSelect = useCallback(
    (reportId: string) => {
      // Don't select if in reorder mode
      if (reorderMode) return;

      actions.setSelectedCustomReport(reportId);
      resetState();
    },
    [actions, resetState, reorderMode],
  );

  const handleCreateNewClick = () => {
    setIsCreatingNew(true);
    setNewReportName("");
  };

  const handleNewReportNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setNewReportName(e.target.value);
  };

  const handleNewReportSubmit = () => {
    if (newReportName.trim() && newReportName !== "Create New Report") {
      const newReportId = actions.addCustomReport(newReportName.trim());
      handleReportSelect(newReportId);
      setIsCreatingNew(false);
      setNewReportName("Create New Report");
    }
  };

  const handleEditReport = (reportId: string, currentName: string) => {
    setEditingReportId(reportId);
    setEditingReportName(currentName);
    setShowOptionsForId(null); // Close options menu when editing starts
  };

  const handleEditReportNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEditingReportName(e.target.value);
  };

  const handleEditReportSubmit = async () => {
    if (editingReportId && editingReportName.trim()) {
      try {
        await axios.put(`/api/users/custom-reports/${editingReportId}`, {
          name: editingReportName.trim(),
        });
        // Update local state after successful API call
        actions.updateCustomReport(editingReportId, editingReportName.trim());
      } catch (error) {
        console.error("Error updating report:", error);
      } finally {
        setEditingReportId(null);
        setEditingReportName("");
      }
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      await axios.delete(`/api/users/custom-reports/${reportId}`);
      // Update local state after successful deletion
      actions.deleteCustomReport(reportId);
    } catch (error) {
      console.error("Error deleting report:", error);
      // Handle the case where deletion is prevented
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        console.log("Cannot delete the last report");
        // You could show a toast notification here if needed
      }
    } finally {
      setShowOptionsForId(null);
    }
  };

  const handleDuplicateReport = async (reportId: string) => {
    try {
      // First update the local state
      actions.duplicateCustomReport(reportId);

      // Then sync with the server
      const response = await axios.post("/api/users/custom-reports", {
        reportId: reportId,
      });

      // Update the state with the server response to ensure consistency
      actions.setCustomReports(response.data.data);
      actions.setSelectedCustomReport(response.data.selectedReport);
    } catch (error) {
      console.error("Error duplicating report:", error);
    } finally {
      setShowOptionsForId(null);
    }
  };

  const handleEnableReorderMode = () => {
    setReorderMode(true);
    setShowOptionsForId(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent, isEditing: boolean) => {
    if (e.key === "Enter") {
      isEditing ? handleEditReportSubmit() : handleNewReportSubmit();
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (maxLength === 0) return text;
    return text?.length > maxLength
      ? text?.substring(0, maxLength) + "..."
      : text;
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!reorderMode) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Make the drag image semi-transparent
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "0.5";
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "1";
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (!reorderMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    if (!reorderMode) return;
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Create new array with reordered items
    const newReports = [...customReports];
    const [draggedItem] = newReports.splice(draggedIndex, 1);
    newReports.splice(dropIndex, 0, draggedItem);

    // Update Zustand store
    actions.reorderCustomReports(newReports);

    // Update backend
    try {
      await axios.put(
        "/api/users/custom-reports/reorder",
        {
          customReports: newReports,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Custom reports order updated successfully");
    } catch (error) {
      console.error("Error updating custom reports order:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios error:", error.response?.data);
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="relative w-fit" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className={cn(
          "flex items-center xl:space-x-2 space-x-1 justify-between xl:px-4 px-2 py-2 h-[60px] focus:outline-none border-white border",
          "xl:text-[10.6px] xl:h-[60px] 2xl:text-base 2xl:rounded-2xl xl:rounded-[10.6px]",
          "h-[36px] text-[8px] flex rounded-lg",
          "2xl:w-full w-full xl:w-full md:w-full",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          // Disabled state styling
          disabled
            ? "bg-kd-secondary-7 text-[#8E8E93] border-none"
            : invertColors
              ? "bg-kd-secondary-7"
              : "border hover:border-kd-lte-blue text-kd-lte-blue",
          !disabled && invertColors
            ? isOpen
              ? "bg-kd-secondary-7 text-kd-secondary-1"
              : "bg-kd-lte-blue text-white"
            : !disabled && isOpen
              ? "bg-kd-secondary-7 text-kd-lte-blue"
              : !disabled && "bg-kd-secondary-7 text-kd-lte-blue",
        )}
      >
        <div className="size-5 2xl:size-7">
          <ReportIcon
            className={cn(
              "mr-2 size-full",
              // The icon is white only when inverted and closed, otherwise it's blue.
              // It's faded when disabled.
              disabled
                ? "text-kd-secondary-4"
                : !disabled && invertColors && !isOpen
                  ? "text-white"
                  : "text-kd-lte-blue",
            )}
          />
        </div>

        <TextComponent
          type="h4"
          className={cn(
            "grow text-nowrap text-left",
            "md:flex hidden",
            disabled
              ? "text-kd-secondary-4"
              : invertColors
                ? ""
                : "text-kd-secondary-1",
          )}
        >
          {truncateText(selectedReport || "Custom Report", truncateLength)}
        </TextComponent>
        {disabled ? (
          <DropdownArrowDownIcon className="text-kd-secondary-4" />
        ) : isOpen ? (
          <DropdownArrowUpIcon
            className={cn(
              !invertColors
                ? isOpen
                  ? "text-kd-lte-blue"
                  : "text-kd-lte-blue"
                : isOpen
                  ? "text-kd-lte-blue"
                  : "text-white",
            )}
          />
        ) : (
          <DropdownArrowDownIcon
            className={cn(!invertColors ? "text-kd-lte-blue" : "text-white")}
          />
        )}
      </button>

      {isOpen && !disabled && (
        <div className="absolute right-0 z-10 mt-2 w-[280px]">
          <div className="overflow-hidden rounded-2xl border border-kd-lte-blue bg-white shadow-lg">
            <div className="max-h-80 overflow-y-auto custom-scrollbar">
              {isEditPage && (
                <div className="sticky top-0 z-30 flex items-center border-b border-dashed border-gray-300 bg-white p-4 hover:bg-kd-secondary-6">
                  <div className="mr-[7.5px] xl:mr-3">
                    <DropdownCreateNewIcon className=" size-3 text-kd-lte-blue xl:size-4" />
                  </div>
                  {isCreatingNew ? (
                    <>
                      <input
                        ref={inputRef}
                        type="text"
                        value={newReportName}
                        onChange={handleNewReportNameChange}
                        onKeyDown={(e) => handleKeyPress(e, false)}
                        className="w-full bg-transparent focus:outline-none text-[10.5px] xl:text-[14px] font-normal text-kd-secondary-1 placeholder:text-kd-secondary-3"
                        placeholder="Enter report name"
                      />
                      <div
                        className="ml-2 cursor-pointer"
                        onClick={handleNewReportSubmit}
                      >
                        <DropdownSaveIcon className="size-3 text-kd-lte-blue xl:size-4" />
                      </div>
                    </>
                  ) : (
                    <TextComponent
                      type="caption"
                      onClick={handleCreateNewClick}
                      className="cursor-pointer text-[#8E8E93]"
                    >
                      Create New Report
                    </TextComponent>
                  )}
                </div>
              )}

              {customReports.map((report, index) => (
                <div
                  key={report.id}
                  draggable={reorderMode && editingReportId !== report.id}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={cn(
                    "group relative flex items-center justify-between p-4 transition-colors hover:bg-kd-secondary-6 hover:text-kd-lte-blue text-[#96A5B8]",
                    reorderMode
                      ? "cursor-grab active:cursor-grabbing"
                      : "cursor-pointer",
                    dragOverIndex === index && draggedIndex !== index
                      ? "border-t-2 border-kd-lte-blue"
                      : "",
                    draggedIndex === index ? "opacity-50" : "opacity-100",
                  )}
                  onMouseEnter={() => setHoveredReportId(report.id)}
                  onMouseLeave={() => setHoveredReportId(null)}
                  onClick={() =>
                    editingReportId !== report.id &&
                    handleReportSelect(report.id)
                  }
                >
                  {/* Show drag handle in reorder mode, otherwise show radio button */}
                  <div className="mr-2">
                    {reorderMode ? (
                      <div
                        className="cursor-grab hover:bg-gray-200 active:cursor-grabbing"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <ReorderIcon className="text-kd-lte-blue" />
                      </div>
                    ) : (
                      <div className="mr-[1.5px] xl:mr-1">
                        {report.id === selectedReportId ? (
                          <DropdownRadioSelectedIcon className=" size-3 text-kd-lte-blue xl:size-4" />
                        ) : (
                          <DropdownRadioUnselectedIcon className="size-3 text-[#96A5B8] group-hover:text-[#336DFF] xl:size-4" />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex grow items-center">
                    {editingReportId === report.id ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editingReportName}
                        onChange={handleEditReportNameChange}
                        onKeyDown={(e) => handleKeyPress(e, true)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-transparent focus:outline-none text-[10.5px] xl:text-[14px] font-normal text-kd-secondary-1 placeholder:text-kd-secondary-3"
                      />
                    ) : (
                      <TextComponent
                        type="caption"
                        className={cn(
                          report.id === selectedReportId && !reorderMode
                            ? "text-[#05004E] font-medium"
                            : "",
                        )}
                      >
                        {report.name}
                        {report.type === "shared" && " (shared)"}
                      </TextComponent>
                    )}
                  </div>

                  {/* Hide options menu in reorder mode */}
                  {!reorderMode && (
                    <div
                      className="flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Show checkmark when editing */}
                      {editingReportId === report.id && (
                        <div
                          className="cursor-pointer"
                          onClick={handleEditReportSubmit}
                        >
                          <DropdownSaveIcon className="size-3 text-kd-lte-blue xl:size-4" />
                        </div>
                      )}

                      {/* Hide ellipsis when editing */}
                      {editingReportId !== report.id && (
                        <button
                          onClick={() =>
                            setShowOptionsForId(
                              showOptionsForId === report.id ? null : report.id,
                            )
                          }
                          className="px-2 text-[#96A5B8] hover:text-gray-600"
                        >
                          <DropDownEllipse className="h-[18px] w-1" />
                        </button>
                      )}

                      {showOptionsForId === report.id && (
                        <div className="fixed z-20">
                          <div className="absolute -top-4 right-2 w-[123px] origin-top-right overflow-hidden rounded-lg border border-kd-lte-blue bg-[#F8F9FA] shadow-lg">
                            <button
                              onClick={() => {
                                handleReportSelect(report.id);
                                window.location.href =
                                  "/dashboard/report-builder";
                              }}
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                handleEditReport(report.id, report.name)
                              }
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Rename
                            </button>
                            <button
                              onClick={() => handleDuplicateReport(report.id)}
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Duplicate
                            </button>
                            <button
                              onClick={handleEnableReorderMode}
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Reorder
                            </button>
                            {report.id !== selectedReportId && (
                              <button
                                onClick={() => handleDeleteReport(report.id)}
                                disabled={customReports.length <= 1}
                                className={cn(
                                  "block w-full border-t border-dashed border-gray-200 px-4 py-2 text-left text-xs",
                                  customReports.length <= 1
                                    ? "cursor-not-allowed text-gray-400 bg-gray-50"
                                    : "text-[#FA5A7D] hover:bg-white cursor-pointer",
                                )}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomReportDropdown;
